import { Platform } from 'react-native';

import { DEV_RU_EDGE_ORIGIN, isRuDevEdgeWebHost } from '../config/ruDevEdge';
import { isRuStagingEdgeWebHost, STAGING_RU_EDGE_ORIGIN } from '../config/stagingEnv';

const RAW_API_URL = process.env.EXPO_PUBLIC_API_URL || 'https://api.agapella.io';

const isWeb = Platform.OS === 'web' && typeof window !== 'undefined';
const host = isWeb ? window.location?.hostname || '' : '';

// Prod web origin. We used to leave API_BASE_URL = '' here so /api/* hit the
// Vercel rewrite → api.agapella.io server-side. Switching to absolute for
// two reasons:
//   1) Bypass Vercel custom-domain edge IPs (216.150.x.x), which are shaped
//      on several RU ISPs to ~340 b/s. Saves a hop and an unreliable edge for
//      everyone.
//      CORE-723 (2026-08-03) — this comment used to add "while the origin IP
//      204.168.236.62 is not [shaped]". That is no longer true: from Russia a
//      response from that address dies around 10-15 KB and the connection it
//      died on goes dead for everything after it. Both chat backends resolve
//      there too. Going direct is therefore no longer the fast path for RU;
//      apiEndpoint.ts (RU-first) reroutes those users to api-ru, and api-ru
//      reaches the origin through the Frankfurt relay. Do not "simplify" this
//      back to a direct hop for RU without re-measuring.
//   2) Fixes a routing collision: /api/practice-rooms/* fetches (e.g.
//      /api/practice-rooms/calendar) were being eaten by the SSR rewrite
//      for /practice-rooms/session/:code and returning the SPA HTML
//      instead of JSON. Direct calls to api.agapella.io skip the rewrite
//      table entirely.
// Backend CORS allows https://app.agapella.io (verified: returns
// access-control-allow-origin + 204 on preflight). Auth is bearer-token
// in Authorization header, no cross-origin cookie dependence.
// Every production host on the agapella.io zone must call the API directly
// (absolute) — the apex `agapella.io`, `www`, `app`, `app-ru`, and any future
// `app-<region>` mirror. app.agapella.io is Vercel-hosted but we bypass its
// edge IPs; the apex and app-ru are served by the RU edge nginx, which does
// NOT carry Vercel's /api rewrite table. Leaving API_BASE_URL='' there makes
// the FE issue relative /api/* that depend on a rewrite the edge may not
// (fully) provide → CORS/404 / shaped-upload on the upload paths (CORE-291,
// extended to the apex). Backend + bucket CORS allow all these agapella.io
// origins (verified: app, app-ru, apex). apiFallback still handles the RU
// api-ru failover.
const isProdAppHost = isWeb && /(^|\.)agapella\.io$/.test(host);

// RU DEV edge. `app.rudev.tmless.org` — and since CORE-1422 the dev frontend
// for Russia, `dev.agdev.tmless.org` — is the Moscow nginx (93.183.74.165)
// that proxies the DEV origins, so the FE served there must talk to the DEV
// edge (`api.rudev.tmless.org` → api.agdev.tmless.org), NOT prod
// api.agapella.io. Used by the e2e runner running from Russia so the whole
// flow (FE shell + REST + hubs) terminates on the stable RU edge instead of
// the DPI-shaped direct hop to Vercel / api.agdev. Mirrors the prod
// app-ru → api-ru pairing. See docs/infra/rudev.tmless.org.nginx.conf.
const isRuDevHost = isWeb && isRuDevEdgeWebHost(host);
const RU_DEV_API = DEV_RU_EDGE_ORIGIN;

// CORE-1589 — the same pairing for the stand: `app.ru.ag1.tmless.org` is the
// Moscow edge in front of the stand, and the bundle it serves is baked with
// `api.ag1.tmless.org` — a leg that dies around 16 KB from Russia. Served from
// the edge, it talks to the stand through `api.ru.ag1.tmless.org`.
const isRuStagingHost = isWeb && isRuStagingEdgeWebHost(host);

// Only *.vercel.app PREVIEW deploys keep relative /api/* so vercel.json's
// rewrite table routes them to the dev API. All agapella.io hosts are
// excluded above (they go direct).
const isVercelHostedWeb = isWeb && !isProdAppHost && !isRuDevHost && host.endsWith('vercel.app');

// For plain fetch calls.
export const API_BASE_URL = (() => {
  if (!isWeb) return RAW_API_URL;          // native + localhost dev
  if (isRuDevHost) return RU_DEV_API;      // app.rudev.tmless.org → api.rudev (dev edge)
  if (isRuStagingHost) return STAGING_RU_EDGE_ORIGIN; // app.ru.ag1 → api.ru.ag1 (stand edge)
  if (isProdAppHost) return RAW_API_URL;   // app.agapella.io → direct api.agapella.io
  if (isVercelHostedWeb) return '';        // preview / apex → Vercel rewrite
  return RAW_API_URL;                      // any other web host
})();

// For SignalR hubs and anything that needs an absolute URL (vercel.json only
// rewrites `/api/...`, not `/hubs/...`).
export const ABSOLUTE_API_URL = isRuDevHost
  ? RU_DEV_API
  : isRuStagingHost ? STAGING_RU_EDGE_ORIGIN : RAW_API_URL;
